import React, { useEffect, useState } from 'react';
import {
    MDBBtn,
    MDBIcon,
    MDBModal,
    MDBModalDialog,
    MDBModalContent,
    MDBModalHeader,
    MDBModalTitle,
    MDBModalBody,
} from 'mdb-react-ui-kit';
import { Carousel, initMDB } from "mdb-ui-kit";
import supabase from '../config/supabaseClient';
import { useNavigate } from 'react-router-dom';
import './Carousel.css'
import { useAuth } from '../context/AuthContext';

initMDB({ Carousel });

export default function App() {
    const navigate = useNavigate();
    const [optCarouselModal, setOptCarouselModal] = useState(false);
    const [elementsCarousel, setElementsCarousel] = useState([]);
    const [originalElements, setOriginalElements] = useState([]);
    const [currentEditIndex, setCurrentEditIndex] = useState(0);
    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const [height, setHeight] = useState(getResponsiveHeight());
    const { isTokenValid } = useAuth();

    function getResponsiveHeight() {
        if (window.innerWidth < 576) return '25vh'; // celular
        if (window.innerWidth < 768) return '50vh'; // tablet
        return '90vh'; // pc
    }

    useEffect(() => {
        const fetchData = async () => {
            const { data, error } = await supabase
                .from('main_carousel_elements')
                .select('*');
            if (error) {
                console.error('Error al cargar datos:', error);
            } else {
                setElementsCarousel(data);
                setOriginalElements(data);
            }
        };

        fetchData();

        const handleResize = () => setHeight(getResponsiveHeight());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (optCarouselModal) {
            setOriginalElements(JSON.parse(JSON.stringify(elementsCarousel)));
        }
    }, [optCarouselModal]);

    useEffect(() => {
        const current = JSON.stringify(elementsCarousel);
        const original = JSON.stringify(originalElements);
        setHasPendingChanges(current !== original);
    }, [elementsCarousel, originalElements]);

    const handleTitleChange = (e) => {
        updateField("title", e.target.value);
    };

    const handleDescriptionChange = (e) => {
        updateField("description", e.target.value);
    };

    const updateField = (field, value) => {
        const updated = [...elementsCarousel];
        updated[currentEditIndex] = { ...updated[currentEditIndex], [field]: value };
        setElementsCarousel(updated);
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];

        if (!file || !['image/png', 'image/jpeg', 'image/jpg', 'image/gif'].includes(file.type)) {
            alert('Por favor carga un archivo de imagen válido (PNG, JPG, JPEG, GIF)');
            return;
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const filePath = `carousel/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('images')
            .upload(filePath, file);

        if (uploadError) {
            console.error('Error al subir imagen:', uploadError);
            alert('Hubo un error al subir la imagen.');
            return;
        }

        const { data: publicUrlData } = supabase
            .storage
            .from('images')
            .getPublicUrl(filePath);

        const publicUrl = publicUrlData.publicUrl;
        updateField("image", publicUrl);
    };

    const handlePositionChange = (e) => {
        const newPosition = Number(e.target.value) - 1;
        const updated = [...elementsCarousel];
        const item = updated.splice(currentEditIndex, 1)[0];
        updated.splice(newPosition, 0, item);
        setElementsCarousel(updated);
        setCurrentEditIndex(newPosition);
    };

    const toggleOpen = () => setOptCarouselModal(!optCarouselModal);

    const handleSave = async () => {
        if (window.confirm("¿Estás seguro de que deseas guardar los cambios?")) {
            const idsToDelete = originalElements.map(item => item.id);

            const { error: deleteError } = await supabase
                .from('main_carousel_elements')
                .delete()
                .in('id', idsToDelete);

            if (deleteError) {
                throw new Error('Error al eliminar los registros anteriores: ' + deleteError.message);
            }

            const updatedElements = elementsCarousel.map(item => ({
                title: item.title,
                description: item.description,
                image: item.image
            }));

            const { error } = await supabase
                .from('main_carousel_elements')
                .upsert(updatedElements, { onConflict: ['id'] });

            if (error) {
                console.error('Error al guardar los cambios:', error);
            } else {
                navigate(0);
            }
        }
    };

    const handleDiscard = () => {
        if (window.confirm("¿Estás seguro de que deseas descartar los cambios?")) {
            setElementsCarousel(JSON.parse(JSON.stringify(originalElements)));
            setHasPendingChanges(false);
        }
    };

    const handleAddNew = () => {
        const newItem = {
            title: "",
            description: "",
            image: ""
        };
        const updated = [...elementsCarousel, newItem];
        setElementsCarousel(updated);
        setCurrentEditIndex(updated.length - 1);
    };

    const handleDelete = async () => {
        if (window.confirm("¿Estás seguro de que deseas eliminar este elemento?")) {
            const elementToDelete = elementsCarousel[currentEditIndex];

            const { error } = await supabase
                .from('main_carousel_elements')
                .delete()
                .eq('id', elementToDelete.id);

            if (error) {
                console.error("Error al eliminar de Supabase:", error.message);
                alert("Ocurrió un error al eliminar el elemento.");
                return;
            }

            navigate(0);
        }
    };

    return (
        <>
            <section id="carousel">
                <div id="carouselPrincipal" className="carousel slide" data-bs-ride="carousel" data-bs-interval="8000">
                    {
                        originalElements.length > 0 && (
                            <>
                                <div className="carousel-inner">
                                    {originalElements.map((item, index) => (
                                        <div
                                            key={index}
                                            className={`carousel-item ${index === 0 ? 'active' : ''}`}
                                        >
                                            <img
                                                src={item.image || "default-image-url"}
                                                className="img-fluid w-100 d-block object-fit-cover"
                                                style={{ height, filter: 'brightness(45%)' }}
                                                alt={`Slide ${index + 1}`}
                                            />

                                            <div className="carousel-caption d-block">
                                                <h5 className="carousel-title">{item.title}</h5>
                                                <p className="carousel-description">{item.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                    {
                                        isTokenValid && (
                                            <MDBBtn
                                                onClick={() => toggleOpen()}
                                                floating
                                                tag='a'
                                                size='lg'
                                                style={{
                                                    backgroundColor: '#eb6106',
                                                    position: 'absolute',
                                                    right: 0,
                                                    margin: '1%',
                                                    zIndex: 10
                                                }}
                                            >
                                                <MDBIcon fas icon='pen' />
                                            </MDBBtn>
                                        )
                                    }
                                </div>
                            </>
                        )
                    }
                    <button className="carousel-control-prev" type="button" data-bs-target="#carouselPrincipal" data-bs-slide="prev">
                        <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                        <span className="visually-hidden">Previous</span>
                    </button>
                    <button className="carousel-control-next" type="button" data-bs-target="#carouselPrincipal" data-bs-slide="next">
                        <span className="carousel-control-next-icon" aria-hidden="true"></span>
                        <span className="visually-hidden">Next</span>
                    </button>
                </div>

                <MDBModal open={optCarouselModal} tabIndex='-1' onClose={() => setOptCarouselModal(false)}>
                    <MDBModalDialog size='lg'>
                        <MDBModalContent>
                            <MDBModalHeader>
                                <MDBModalTitle>Editar carrusel principal</MDBModalTitle>
                                <MDBBtn className='btn-close' color='none' onClick={toggleOpen}></MDBBtn>
                            </MDBModalHeader>
                            <MDBModalBody>
                                <div className="text-center mb-4">
                                    <div className="position-relative text-center mb-4">
                                        <img
                                            src={elementsCarousel[currentEditIndex]?.image}
                                            className="d-block w-100"
                                            alt={`Slide ${currentEditIndex + 1}`}
                                            style={{ maxHeight: '400px', objectFit: 'cover', borderRadius: '8px', filter: 'brightness(45%)' }}
                                        />
                                        <div className="position-absolute bottom-0 start-0 end-0 p-3 text-white"
                                            style={{
                                                background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))',
                                                borderBottomLeftRadius: '8px',
                                                borderBottomRightRadius: '8px'
                                            }}>
                                            <h5 className="mb-1 carousel-title">{elementsCarousel[currentEditIndex]?.title || 'Sin título'}</h5>
                                            <p className="mb-0 carousel-description">{elementsCarousel[currentEditIndex]?.description || 'Sin descripción'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Navegación */}
                                <div className="d-flex justify-content-center gap-3 mb-4">
                                    <button className="btn btn-outline-primary"
                                        onClick={() => setCurrentEditIndex(prev => Math.max(prev - 1, 0))}
                                        disabled={currentEditIndex === 0}
                                    >
                                        <i className="fas fa-chevron-left me-2"></i>Anterior
                                    </button>
                                    <button className="btn btn-outline-primary"
                                        onClick={() => setCurrentEditIndex(prev => Math.min(prev + 1, elementsCarousel.length - 1))}
                                        disabled={currentEditIndex === elementsCarousel.length - 1}
                                    >
                                        Siguiente<i className="fas fa-chevron-right ms-2"></i>
                                    </button>
                                </div>

                                {/* Formulario */}
                                <div className='container'>
                                    <form className="row g-3 w-100" onSubmit={(e) => e.preventDefault()}>
                                        <div className="col-12">
                                            <label htmlFor="inputTitle" className="form-label">Título</label>
                                            <input
                                                type="text"
                                                value={elementsCarousel[currentEditIndex]?.title || ''}
                                                className="form-control"
                                                id="inputTitle"
                                                onInput={handleTitleChange}
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label htmlFor="inputDescription" className="form-label">Descripción</label>
                                            <textarea
                                                value={elementsCarousel[currentEditIndex]?.description || ''}
                                                className="form-control"
                                                id="inputDescription"
                                                onInput={handleDescriptionChange}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="inputImage" className="form-label">Imagen</label>
                                            <input
                                                type="file"
                                                accept="image/png, image/jpeg, image/jpg, image/gif"
                                                className="form-control"
                                                id="inputImage"
                                                onChange={handleImageChange}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="inputState" className="form-label">Posición</label>
                                            <select
                                                id="inputState"
                                                className="form-select"
                                                value={currentEditIndex + 1}
                                                onChange={handlePositionChange}
                                            >
                                                {elementsCarousel.map((_, index) => (
                                                    <option key={index} value={index + 1}>{index + 1}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Botones */}
                                        <div className="col-12 d-flex gap-3 justify-content-around mt-4 mb-3 flex-wrap">
                                            <button
                                                type="button"
                                                className={`btn btn-primary ${!hasPendingChanges ? 'disabled' : ''}`}
                                                onClick={handleSave}
                                            >
                                                <i className="fa-solid fa-floppy-disk me-2"></i>
                                                Guardar cambios
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-primary ${!hasPendingChanges ? 'disabled' : ''}`}
                                                onClick={handleDiscard}
                                            >
                                                <i className="fa-solid fa-rotate-left me-2"></i>
                                                Descartar cambios
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-success"
                                                onClick={handleAddNew}
                                            >
                                                <i className="fa-solid fa-plus me-2"></i>
                                                Agregar nuevo
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={handleDelete}
                                                disabled={elementsCarousel.length <= 1}
                                            >
                                                <i className="fa-solid fa-trash me-2"></i>
                                                Eliminar
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </MDBModalBody>
                        </MDBModalContent>
                    </MDBModalDialog>
                </MDBModal>
            </section >
        </>

    );
}